import { APIProvider } from '@vis.gl/react-google-maps';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getTenantSettings, getUser } from '@s20/selectors/features/common.selectors';
import { httpClient } from '@s20/services/httpClient';
import { FormCircularProgress, FormValidated } from '../../common/forms/FormElements';
import Input from '../../common/forms/Input';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { Split } from '../../common/Split';
import { AccordionCollapse } from '../../common/AccordionCollapse';
import { ApiRoutes } from '../../Common';
import { DispatchOffice } from './DispatchOffice';
import { Jobs } from './Jobs';
import { MapData } from './MapData';
import { MapDataContext } from './MapDataContext';
import { MapView } from './MapView';
import './index.scss';

const assignmentStatusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'notScheduled', label: 'Not Scheduled' },
];

const confirmationStatusOptions = [
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'notConfirmed', label: 'Not Confirmed' },
];

const onCallStatusOptions = [
    { value: 'onCall', label: 'On Call' },
    { value: 'notOnCall', label: 'Not On Call' },
];

export const DispatchPlusMap = () => {
    const { googleMapsApiKey, mapId } = useSelector(getTenantSettings);
    const user = useSelector(getUser);

    const { data: dispatchLocations, isLoading } = useQuery({
        queryKey: ['dispatchOffices'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
    });

    if (isLoading) {
        return <FormCircularProgress />;
    }

    const userDispatchCompanyId = user?.dispatchCompanyId;
    const hasUserDispatch = userDispatchCompanyId
        && dispatchLocations.some((loc) => loc.value === userDispatchCompanyId);

    const defaultLocationId = hasUserDispatch ? userDispatchCompanyId : null;
    const defaultLocation = defaultLocationId
        ? dispatchLocations.find((loc) => loc.value === defaultLocationId)
        : null;

    return (
        <FormValidated
            id="dispatchPlusMapForm"
            className="dispatchPaneContainer"
            initialValues={{
                dispatchOfficeLocationId: defaultLocationId,
                employeeDispatchLocation: defaultLocation ? [defaultLocation.value] : [],
                selectedDate: moment().format('YYYY-MM-DD'),
            }}
        >
            {() => (
                <MapData>
                    <Split
                        id="dispatchPane"
                        className="dispatchPane"
                        sizes={[20, 80]}
                        minSize={[324, 300]}
                    >
                        <div id="dispatchLeft">
                            <Input
                                type="date"
                                id="selectedDate"
                                name="selectedDate"
                                label="Date"
                            />

                            <AccordionCollapse
                                title="Jobs Filters"
                                defaultOpen
                            >
                                <div className="p-4">
                                    <DispatchOffice
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                    />

                                    <Jobs />
                                </div>
                            </AccordionCollapse>

                            <AccordionCollapse
                                title="Employee Filters"
                                defaultOpen
                            >
                                <div className="p-4">
                                    <DispatchOffice
                                        id="employeeDispatchLocation"
                                        name="employeeDispatchLocation"
                                        label="Dispatch Location"
                                        placeholder="Select dispatch locations..."
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                        isMulti
                                        asPrimitive
                                    />

                                    <Input
                                        id="employeeSearch"
                                        name="employeeSearch"
                                        label="Employee Search"
                                        placeholder="Search by name, city, or address..."
                                    />

                                    <MapDataContext.Consumer>
                                        {(data) => (
                                            <>
                                                <ValidatedSelect
                                                    id="attendanceFilters"
                                                    name="attendanceFilters"
                                                    label="Daily Attendance"
                                                    isMulti
                                                    options={data.dailyAttendanceEvents}
                                                    disabled={!data.dailyAttendanceEvents}
                                                    placeholder="Select attendance events..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                    asPrimitive
                                                />

                                                <ValidatedSelect
                                                    id="longTermStatusFilters"
                                                    name="longTermStatusFilters"
                                                    label="Long Term Status"
                                                    isMulti
                                                    options={data.longTermStatuses}
                                                    placeholder="Select long term statuses..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                    asPrimitive
                                                />

                                                <ValidatedSelect
                                                    id="onCallStatusFilter"
                                                    name="onCallStatusFilter"
                                                    label="On Call Status"
                                                    options={onCallStatusOptions}
                                                    isClearable
                                                    placeholder="Select status..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                />

                                                <ValidatedSelect
                                                    id="jobCancellationFilters"
                                                    name="jobCancellationFilters"
                                                    label="Job Cancellations"
                                                    isMulti
                                                    options={data.jobCancellationTypes}
                                                    placeholder="Select cancellations..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                    asPrimitive
                                                />

                                                <ValidatedSelect
                                                    id="assignmentStatusFilter"
                                                    name="assignmentStatusFilter"
                                                    label="Assignment Status"
                                                    options={assignmentStatusOptions}
                                                    isClearable
                                                    placeholder="Select status..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                />

                                                <ValidatedSelect
                                                    id="confirmationStatusFilter"
                                                    name="confirmationStatusFilter"
                                                    label="Confirmation Status"
                                                    options={confirmationStatusOptions}
                                                    isClearable
                                                    placeholder="Select status..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                />

                                                <ValidatedSelect
                                                    id="employeeCertificationFilters"
                                                    name="employeeCertificationFilters"
                                                    label="Employee Certifications"
                                                    isMulti
                                                    options={data.certifications}
                                                    placeholder="Select Employee Certifications..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                    asPrimitive
                                                />

                                                <ValidatedSelect
                                                    id="userGroupFilters"
                                                    name="userGroupFilters"
                                                    label="User Groups"
                                                    isMulti
                                                    options={data.userGroups}
                                                    placeholder="Select user groups..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="auto"
                                                    asPrimitive
                                                />
                                            </>
                                        )}
                                    </MapDataContext.Consumer>
                                </div>
                            </AccordionCollapse>
                        </div>

                        <div id="dispatchRight">
                            <APIProvider
                                apiKey={googleMapsApiKey}
                                libraries={['marker']}
                                version="quarterly"
                            >
                                <MapView
                                    mapId={mapId}
                                />
                            </APIProvider>
                        </div>
                    </Split>
                </MapData>
            )}
        </FormValidated>
    );
};